import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { VideoSettingsState } from '@/features/settings/hooks/useVideoSettings'
import type { VideoDraft, VideoKind } from '@/features/settings/types'
import { ConnectionTestRow } from './ConnectionTestRow'

/** One labelled text field of the form; a secret is a password field that keeps the saved one when blank. */
function Field({
  id,
  label,
  value,
  onChange,
  secret,
  saved,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  secret?: boolean
  saved?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={secret ? 'password' : 'text'}
        autoComplete={secret ? 'new-password' : 'off'}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={
          secret ? t(saved ? 'settings.video.keepSecret' : 'settings.video.enterSecret') : undefined
        }
      />
    </div>
  )
}

/** A part's connection test, with the page's own words. */
function ConnectionTest({ kind, state }: { kind: VideoKind; state: VideoSettingsState }) {
  const { t } = useTranslation()
  return (
    <ConnectionTestRow
      id={kind}
      result={state.testResults[kind]}
      testing={state.testingKind === kind}
      onTest={() => void state.test(kind)}
      labels={{ test: t('settings.video.test'), working: t('settings.video.working') }}
    />
  )
}

/** The engine: where it is and the key this tenant speaks to it with. */
function EngineFields({ draft, state }: { draft: VideoDraft; state: VideoSettingsState }) {
  const { t } = useTranslation()
  const engine = draft.engine
  const set = (part: Partial<VideoDraft['engine']>) =>
    state.update('engine', { ...engine, ...part })
  return (
    <div className="space-y-3">
      <h3 className="font-medium">{t('settings.video.engine')}</h3>
      <Field
        id="video-engine-url"
        label={t('settings.video.engineUrl')}
        value={engine.url}
        onChange={url => set({ url })}
      />
      <Field
        id="video-engine-key"
        label={t('settings.video.engineKey')}
        value={engine.api_key}
        onChange={api_key => set({ api_key })}
        secret
        saved={engine.has_api_key}
      />
      <ConnectionTest kind="engine" state={state} />
    </div>
  )
}

/** The exchange bucket a recording goes through: the S3 endpoint, the bucket, the tenant's prefix and its key. */
function ExchangeFields({ draft, state }: { draft: VideoDraft; state: VideoSettingsState }) {
  const { t } = useTranslation()
  const exchange = draft.exchange
  const set = (part: Partial<VideoDraft['exchange']>) =>
    state.update('exchange', { ...exchange, ...part })
  return (
    <div className="space-y-3">
      <h3 className="font-medium">{t('settings.video.exchange')}</h3>
      <Field
        id="video-exchange-endpoint"
        label={t('settings.video.endpoint')}
        value={exchange.endpoint_url}
        onChange={endpoint_url => set({ endpoint_url })}
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <Field
          id="video-exchange-bucket"
          label={t('settings.video.bucket')}
          value={exchange.bucket}
          onChange={bucket => set({ bucket })}
        />
        <Field
          id="video-exchange-tenant"
          label={t('settings.video.tenant')}
          value={exchange.tenant}
          onChange={tenant => set({ tenant })}
        />
        <Field
          id="video-exchange-region"
          label={t('settings.video.region')}
          value={exchange.region}
          onChange={region => set({ region })}
        />
      </div>
      <Field
        id="video-exchange-access-key"
        label={t('settings.video.accessKeyId')}
        value={exchange.access_key_id}
        onChange={access_key_id => set({ access_key_id })}
      />
      <Field
        id="video-exchange-secret"
        label={t('settings.video.secret')}
        value={exchange.secret_access_key}
        onChange={secret_access_key => set({ secret_access_key })}
        secret
        saved={exchange.has_secret_access_key}
      />
      <ConnectionTest kind="exchange" state={state} />
    </div>
  )
}

/**
 * Admin → Video engine (PRD-0074 SC-003): the engine and the exchange bucket this deployment's
 * recordings go through, as the audio services are set. Blank secrets keep the saved ones; each
 * part can be tested with what is about to be saved.
 */
export function VideoSection({ state }: { state: VideoSettingsState }) {
  const { t } = useTranslation()
  const { draft, saving, message, failed } = state
  const busy = saving || state.testingKind !== null
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.sections.video')}</CardTitle>
        <CardDescription>{t('settings.video.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {message && (
          <p
            role={failed ? 'alert' : 'status'}
            className={failed ? 'text-destructive' : 'text-muted-foreground'}
          >
            {message}
          </p>
        )}
        {!draft && !failed && <p role="status">{t('settings.video.loading')}</p>}
        {draft && (
          <fieldset disabled={busy} className="space-y-6">
            <EngineFields draft={draft} state={state} />
            <ExchangeFields draft={draft} state={state} />
            <p className="text-sm text-muted-foreground">{t('settings.video.hosts')}</p>
            <Button type="button" onClick={() => void state.save()}>
              {t(saving ? 'settings.video.working' : 'settings.video.save')}
            </Button>
          </fieldset>
        )}
      </CardContent>
    </Card>
  )
}
